import React from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Minus, Sparkles, Calculator } from "lucide-react";

export function TrackingItem({ icon: Icon, label, value, goal, unit, color, onIncrement, onDecrement, isDark, hint, isToday, calcKey, calcLabel, onOpenCalc }) {
  const percentage = goal > 0 ? Math.min((value / goal) * 100, 100) : 0;
  const isOver = value > goal && goal > 0;
  const fmt = (v) => typeof v === 'number' ? (v % 1 === 0 ? v : v.toFixed(1)) : v;
  const hasCalc = !!calcKey;
  const noGoal = !goal || goal === 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-3.5 h-3.5" style={{ color }} />
          </div>
          <div>
            <p className={`text-xs font-medium ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}>{label}</p>
            <p className={`text-[10px] ${isOver ? 'text-[#EF4444] font-medium' : isDark ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
              {fmt(value)} / {fmt(goal)} {unit}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-0.5">
          {hint && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Sparkles className="w-3 h-3 text-[#F59E0B] mr-0.5 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[250px] text-xs"><p>{hint}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {hasCalc && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onOpenCalc?.(calcKey)}
                    className={`p-1 rounded-md mr-0.5 transition-colors ${isDark ? 'hover:bg-[hsl(223,47%,15%)] text-[#0EA5E9]' : 'hover:bg-blue-50 text-[#0EA5E9]'}`}
                    data-testid={`calc-link-${calcKey}`}
                  >
                    <Calculator className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs"><p>{calcLabel}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isToday && (
            <>
              <Button variant="ghost" size="icon" className={`h-6 w-6 rounded-md ${isDark ? 'hover:bg-[hsl(223,47%,15%)]' : 'hover:bg-[#F1F5F9]'}`}
                onClick={onDecrement}><Minus className="w-3 h-3" /></Button>
              <Button variant="ghost" size="icon" className={`h-6 w-6 rounded-md ${isDark ? 'hover:bg-[hsl(223,47%,15%)]' : 'hover:bg-[#F1F5F9]'}`}
                onClick={onIncrement}><Plus className="w-3 h-3" /></Button>
            </>
          )}
        </div>
      </div>
      <div className="relative h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? '#374151' : '#E2E8F0' }}>
        <div className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
          style={{ width: `${percentage}%`, backgroundColor: isOver ? '#EF4444' : color }} />
      </div>
    </div>
  );
}
